/**
 * LicenseGuard.js
 * ─────────────────────────────────────────────────────────────────────────────
 * Drop this single file into any React project, wrap your <App /> with it, done.
 *
 * Usage in index.js (or wherever your root render lives):
 *
 *   import LicenseGuard from './LicenseGuard';
 *
 *   root.render(
 *     <LicenseGuard>
 *       <App />
 *     </LicenseGuard>
 *   );
 *
 * How it works:
 *  1. On mount, reads any previously saved license key from localStorage.
 *  2. Calls the license API with the key to validate it directly.
 *  3. If the API returns StatusCode 200 and Result "True" → renders children normally.
 *  4. If no key, wrong key, or API returns error/500 → renders the blocked screen.
 *  5. Re-validates silently every 60 seconds — system blocks within ~1 minute of server deactivation.
 *  6. Nothing else in the project needs to change.
 *
 * Config (change only these two constants if you reuse this file elsewhere):
 */

const LICENSE_API_BASE_URL =
  "https://dtsclientservicebackend.dockyardsoftware.com/Licenses/GetLicensebykey";
const STORAGE_KEY = "dockyard_license_key";
const REVALIDATE_INTERVAL_MS = 60 * 1000; // 60 seconds — blocks within ~1 min of server deactivation

// ─────────────────────────────────────────────────────────────────────────────

import React, { useState, useEffect, useCallback, useRef } from "react";

/* ── CSS injected once into <head> — no external stylesheet needed ── */

let _cssInjected = false;
function injectCSS() {
  if (_cssInjected || typeof document === "undefined") return;
  _cssInjected = true;
  const style = document.createElement("style");
  style.textContent = `
    @keyframes lic-spin    { to { transform: rotate(360deg); } }
    @keyframes lic-fadein  { from { opacity:0; transform:translateY(14px); } to { opacity:1; transform:translateY(0); } }
    @keyframes lic-pulse   { 0%,100% { opacity:1; } 50% { opacity:0.4; } }
    @keyframes lic-modal-in{ from { opacity:0; transform:scale(0.93) translateY(12px); } to { opacity:1; transform:scale(1) translateY(0); } }
    .lic-card        { animation: lic-fadein 0.48s cubic-bezier(0.16,1,0.3,1) both; }
    .lic-modal-card  { animation: lic-modal-in 0.3s cubic-bezier(0.16,1,0.3,1) both; }
    .lic-input:focus { border-color:#7c3aed !important; box-shadow:0 0 0 3px rgba(124,58,237,0.18) !important; }
    .lic-btn-act:hover:not(:disabled)  { opacity:0.88; transform:translateY(-1px); }
    .lic-btn-act:active:not(:disabled) { transform:translateY(0); }
    .lic-contact-btn:hover { background:rgba(124,58,237,0.22) !important; }
    .lic-copy-btn:hover    { background:rgba(255,255,255,0.07) !important; }
    .lic-close-btn:hover   { background:rgba(255,255,255,0.07) !important; }
  `;
  document.head.appendChild(style);
}

/* ── Design tokens ── */
const C = {
  bg:          "#0c0c1d",
  card:        "#12122b",
  cardBorder:  "#2a2a5a",
  purple:      "#7c3aed",
  purpleL:     "#a78bfa",
  purpleDim:   "rgba(124,58,237,0.14)",
  purpleGlow:  "rgba(124,58,237,0.28)",
  text:        "#e4e0ff",
  muted:       "#8880b8",
  dim:         "#55507a",
  green:       "#22c55e",
  greenDim:    "rgba(34,197,94,0.12)",
  red:         "#ef4444",
  redDim:      "rgba(239,68,68,0.11)",
  inputBg:     "#09091a",
  divider:     "#1c1c3c",
};

/* ── Inline SVG icons (zero deps) ── */
const Ico = ({ d, size = 16, stroke = "currentColor", fill = "none", sw = 2, style: s, ...r }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill={fill}
    stroke={stroke} strokeWidth={sw} strokeLinecap="round" strokeLinejoin="round" style={s} {...r}>
    {[].concat(d).map((p, i) => <path key={i} d={p} />)}
  </svg>
);
const PhoneIco = (p) => <Ico {...p} d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07A19.5 19.5 0 0 1 4.69 13 19.79 19.79 0 0 1 1.61 4.44 2 2 0 0 1 3.6 2.25h3a2 2 0 0 1 2 1.72c.127.96.361 1.903.7 2.81a2 2 0 0 1-.45 2.11L7.91 9.91a16 16 0 0 0 6.13 6.13l1.27-1.27a2 2 0 0 1 2.11-.45c.907.339 1.85.573 2.81.7A2 2 0 0 1 22 16.92z" />;
const MailIco = (p) => <Ico {...p} d={["M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z","M22 6l-10 7L2 6"]} />;
const KeyIco  = (p) => <Ico {...p} d={["M21 2l-2 2m-7.61 7.61a5.5 5.5 0 1 1-7.778 7.778 5.5 5.5 0 0 1 7.777-7.777zm0 0L15.5 7.5m0 0l3 3L22 7l-3-3m-3.5 3.5L19 4"]} />;
const AlertIco= (p) => <Ico {...p} d={["M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z","M12 9v4","M12 17h.01"]} />;
const CheckIco= (p) => <Ico {...p} d="M20 6L9 17l-5-5" />;
const XBtn    = (p) => <Ico {...p} d={["M18 6L6 18","M6 6l12 12"]} />;
const CopyIco = (p) => <Ico {...p} d={["M8 17.929H6c-1.105 0-2-.912-2-2.036V5.036C4 3.912 4.895 3 6 3h8c1.105 0 2 .912 2 2.036v1.866m-6 .17h8c1.105 0 2 .91 2 2.035v10.857C20 21.088 19.105 22 18 22h-8c-1.105 0-2-.912-2-2.036V9.107c0-1.124.895-2.036 2-2.036z"]} />;

/* ── Core API helpers ── */

/**
 * Validates a license key against the GetLicensebykey API.
 * Returns true if valid and active (StatusCode 200, Result "True").
 * Returns false if invalid, inactive, or API returns error (e.g. 500).
 */
async function validateLicenseKey(key) {
  if (!key || !key.trim()) return false;
  try {
    const url = `${LICENSE_API_BASE_URL}?LicenseKey=${encodeURIComponent(key.trim())}`;
    const res = await fetch(url, {
      method: "GET",
      headers: { "Content-Type": "application/json" },
      cache: "no-store",
    });
    if (!res.ok) return false; // 500 or other error = invalid/inactive key
    const data = await res.json();
    return data?.StatusCode === 200 && String(data?.Result).toLowerCase() === "true";
  } catch {
    return false;
  }
}

/* ─────────────────────────────────────────────────────────────────────────────
   Contact Modal
───────────────────────────────────────────────────────────────────────────── */
function ContactModal({ onClose }) {
  const [copied, setCopied] = useState("");

  const copy = (text, label) => {
    navigator.clipboard?.writeText(text).catch(() => {});
    setCopied(label);
    setTimeout(() => setCopied(""), 2200);
  };

  const rowStyle = {
    display: "flex", alignItems: "center",
    justifyContent: "space-between", marginBottom: "8px",
  };
  const copyBtnStyle = (id) => ({
    background: "transparent", border: "none", cursor: "pointer",
    padding: "4px 10px", borderRadius: "7px",
    color: copied === id ? C.green : C.muted,
    display: "flex", alignItems: "center", gap: "5px",
    fontSize: "11px", transition: "background 0.18s, color 0.18s",
  });
  const sectionBox = {
    background: C.inputBg, border: `1px solid ${C.divider}`,
    borderRadius: "13px", padding: "15px 16px", marginBottom: "10px",
  };
  const sectionLabel = {
    display: "flex", alignItems: "center", gap: "7px",
    marginBottom: "11px",
  };

  return (
    <div style={{
      position: "fixed", inset: 0, zIndex: 100001,
      display: "flex", alignItems: "center", justifyContent: "center",
      background: "rgba(0,0,0,0.78)", backdropFilter: "blur(5px)", padding: "16px",
    }}>
      <div className="lic-modal-card" style={{
        background: C.card, border: `1px solid ${C.cardBorder}`,
        borderRadius: "22px", width: "100%", maxWidth: "420px",
        overflow: "hidden", position: "relative",
        boxShadow: "0 24px 64px rgba(0,0,0,0.55), 0 0 0 1px rgba(124,58,237,0.12)",
      }}>
        {/* Accent bar */}
        <div style={{ height: "4px", background: "linear-gradient(90deg,#7c3aed,#a78bfa,#7c3aed)" }} />

        <div style={{ padding: "24px 24px 26px" }}>
          {/* Header row */}
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: "18px" }}>
            <div style={{ display: "flex", alignItems: "center", gap: "11px" }}>
              {/* Small logo */}
              <div style={{
                width: "42px", height: "42px", borderRadius: "12px", overflow: "hidden",
                border: `1px solid ${C.cardBorder}`, flexShrink: 0,
              }}>
                <img src="/dockyard-logo.png" alt="Dockyard"
                  style={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }}
                  onError={(e) => {
                    e.target.parentElement.style.background = "linear-gradient(135deg,#7c3aed,#6d28d9)";
                    e.target.style.display = "none";
                  }} />
              </div>
              <div>
                <p style={{ margin: 0, color: C.text, fontWeight: 700, fontSize: "15px" }}>Contact Us</p>
                <p style={{ margin: 0, color: C.muted, fontSize: "11px" }}>Dockyard Software</p>
              </div>
            </div>
            <button className="lic-close-btn" onClick={onClose} style={{
              background: "transparent", border: "none", cursor: "pointer",
              padding: "6px", borderRadius: "9px", color: C.muted, transition: "background 0.18s",
            }}>
              <XBtn size={18} />
            </button>
          </div>

          {/* Renewal notice */}
          <div style={{
            background: C.purpleDim, border: `1px solid ${C.purpleGlow}`,
            borderRadius: "12px", padding: "13px 15px", marginBottom: "18px",
            display: "flex", gap: "10px", alignItems: "flex-start",
          }}>
            <AlertIco size={15} stroke={C.purpleL} style={{ flexShrink: 0, marginTop: "1px" }} />
            <p style={{ margin: 0, color: C.purpleL, fontSize: "12.5px", lineHeight: 1.65 }}>
              To <strong>renew</strong> an existing license or <strong>purchase a new license key</strong>,
              please reach out to our support team using the contact details below.
              We'll get you back up and running as quickly as possible.
            </p>
          </div>

          {/* Phone section */}
          <div style={sectionBox}>
            <div style={sectionLabel}>
              <PhoneIco size={14} stroke={C.purpleL} />
              <span style={{ color: C.muted, fontSize: "11px", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.06em" }}>
                Call Us
              </span>
            </div>
            {["+94 11 252 0388", "+94 77 697 0808"].map((num) => (
              <div key={num} style={rowStyle}>
                <span style={{ color: C.text, fontSize: "14px", fontFamily: "monospace", letterSpacing: "0.04em" }}>
                  {num}
                </span>
                <button className="lic-copy-btn" onClick={() => copy(num, num)} style={copyBtnStyle(num)}>
                  {copied === num ? <CheckIco size={12} stroke={C.green} /> : <CopyIco size={12} />}
                  {copied === num ? "Copied!" : "Copy"}
                </button>
              </div>
            ))}
          </div>

          {/* Email section */}
          <div style={sectionBox}>
            <div style={sectionLabel}>
              <MailIco size={14} stroke={C.purpleL} />
              <span style={{ color: C.muted, fontSize: "11px", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.06em" }}>
                Email Us
              </span>
            </div>
            <div style={rowStyle}>
              <span style={{ color: C.text, fontSize: "13px", fontFamily: "monospace" }}>
                info@dockyardsoftware.com
              </span>
              <button className="lic-copy-btn" onClick={() => copy("info@dockyardsoftware.com", "email")} style={copyBtnStyle("email")}>
                {copied === "email" ? <CheckIco size={12} stroke={C.green} /> : <CopyIco size={12} />}
                {copied === "email" ? "Copied!" : "Copy"}
              </button>
            </div>
          </div>

          {/* Close button */}
          <button className="lic-btn-act" onClick={onClose} style={{
            marginTop: "6px", width: "100%", padding: "12px",
            background: "linear-gradient(135deg,#7c3aed,#6d28d9)",
            color: "#fff", border: "none", borderRadius: "12px",
            fontWeight: 700, fontSize: "14px", cursor: "pointer",
            transition: "opacity 0.2s, transform 0.15s",
          }}>
            Close
          </button>
        </div>
      </div>
    </div>
  );
}

/* ─────────────────────────────────────────────────────────────────────────────
   Checking / boot screen
───────────────────────────────────────────────────────────────────────────── */
function CheckingScreen() {
  return (
    <div style={{
      position: "fixed", inset: 0, zIndex: 99999, background: C.bg,
      display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center",
      gap: "22px", fontFamily: "'Segoe UI', system-ui, sans-serif",
    }}>
      <div style={{
        width: "76px", height: "76px", borderRadius: "20px", overflow: "hidden",
        boxShadow: "0 8px 32px rgba(124,58,237,0.3)",
      }}>
        <img src="/dockyard-logo.png" alt="Dockyard Software"
          style={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }}
          onError={(e) => {
            e.target.parentElement.style.background = "linear-gradient(135deg,#7c3aed,#6d28d9)";
            e.target.style.display = "none";
          }} />
      </div>
      <div style={{ display: "flex", alignItems: "center", gap: "10px", color: C.muted, fontSize: "13px" }}>
        <span style={{
          display: "inline-block", width: "16px", height: "16px",
          border: `2px solid ${C.divider}`, borderTopColor: C.purpleL,
          borderRadius: "50%", animation: "lic-spin 0.72s linear infinite",
        }} />
        Verifying license…
      </div>
    </div>
  );
}

/* ─────────────────────────────────────────────────────────────────────────────
   Main LicenseGuard
───────────────────────────────────────────────────────────────────────────── */
export default function LicenseGuard({ children }) {
  injectCSS();

  const [phase, setPhase]               = useState("checking");
  const [savedKey, setSavedKey]         = useState(() => localStorage.getItem(STORAGE_KEY) || "");
  const [inputKey, setInputKey]         = useState("");
  const [activating, setActivating]     = useState(false);
  const [error, setError]               = useState("");
  const [success, setSuccess]           = useState("");
  const [showContact, setShowContact]   = useState(false);
  const timerRef = useRef(null);

  /* validate key against API — returns true if valid & active */
  const validate = useCallback(async (keyToCheck) => {
    if (!keyToCheck) return false;
    try {
      return await validateLicenseKey(keyToCheck);
    } catch { return false; }
  }, []);

  /* run validation; silent = don't show the checking screen (background re-check) */
  const runValidation = useCallback(async (key, silent = false) => {
    if (!silent) setPhase("checking");
    const isValid = await validate(key);
    if (isValid) {
      localStorage.setItem(STORAGE_KEY, key.trim());
      setSavedKey(key.trim());
      setPhase("active");
    } else {
      setPhase("blocked");
    }
  }, [validate]);

  /* mount: initial check + periodic silent re-check */
  useEffect(() => {
    const key = localStorage.getItem(STORAGE_KEY) || "";
    runValidation(key, false);
    timerRef.current = setInterval(() => {
      const k = localStorage.getItem(STORAGE_KEY) || "";
      runValidation(k, true);
    }, REVALIDATE_INTERVAL_MS);
    return () => clearInterval(timerRef.current);
  }, [runValidation]);

  /* user activates a new key */
  const handleActivate = async () => {
    const key = inputKey.trim();
    if (!key) { setError("Please enter a license key."); return; }
    setError(""); setSuccess("");
    setActivating(true);
    try {
      const isValid = await validateLicenseKey(key);
      if (isValid) {
        localStorage.setItem(STORAGE_KEY, key);
        setSavedKey(key);
        setSuccess("License activated successfully! Loading system…");
        setTimeout(() => setPhase("active"), 1000);
      } else {
        setError(
          "This license key is invalid or inactive. Please check the key and try again, or contact Dockyard Software to renew."
        );
      }
    } catch {
      setError("Unable to reach the license server. Please check your connection and try again.");
    } finally {
      setActivating(false);
    }
  };

  /* ── Render ── */
  if (phase === "checking") return <CheckingScreen />;
  if (phase === "active")   return children;

  /* blocked */
  const hasExistingKey = !!savedKey;

  return (
    <>
      {showContact && <ContactModal onClose={() => setShowContact(false)} />}

      <div style={{
        position: "fixed", inset: 0, zIndex: 99999,
        display: "flex", alignItems: "center", justifyContent: "center",
        background: C.bg, padding: "16px", overflowY: "auto",
        fontFamily: "'Segoe UI', system-ui, -apple-system, sans-serif",
      }}>
        {/* Background glow blobs */}
        <div style={{
          position: "absolute", top: "8%", left: "18%", width: "360px", height: "360px",
          borderRadius: "50%", pointerEvents: "none", filter: "blur(50px)",
          background: "radial-gradient(circle,rgba(124,58,237,0.13) 0%,transparent 70%)",
        }} />
        <div style={{
          position: "absolute", bottom: "10%", right: "12%", width: "300px", height: "300px",
          borderRadius: "50%", pointerEvents: "none", filter: "blur(55px)",
          background: "radial-gradient(circle,rgba(109,40,217,0.1) 0%,transparent 70%)",
        }} />

        {/* Card */}
        <div className="lic-card" style={{
          background: C.card, border: `1px solid ${C.cardBorder}`,
          borderRadius: "26px", width: "100%", maxWidth: "470px",
          overflow: "hidden", position: "relative",
          boxShadow: "0 36px 90px rgba(0,0,0,0.65), 0 0 0 1px rgba(124,58,237,0.1)",
        }}>
          {/* Top accent bar */}
          <div style={{ height: "4px", background: "linear-gradient(90deg,#7c3aed,#a78bfa,#c084fc,#a78bfa,#7c3aed)" }} />

          <div style={{ padding: "36px 34px 30px" }}>

            {/* ── Logo & branding ── */}
            <div style={{ display: "flex", flexDirection: "column", alignItems: "center", marginBottom: "30px" }}>
              <div style={{
                width: "88px", height: "88px", borderRadius: "24px",
                overflow: "hidden", marginBottom: "16px",
                boxShadow: "0 10px 40px rgba(124,58,237,0.4), 0 0 0 2px rgba(124,58,237,0.22)",
              }}>
                <img
                  src="/dockyard-logo.png"
                  alt="Dockyard Software"
                  style={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }}
                  onError={(e) => {
                    e.target.parentElement.style.background = "linear-gradient(135deg,#7c3aed,#6d28d9)";
                    e.target.style.display = "none";
                  }}
                />
              </div>
              <p style={{ margin: 0, color: C.text, fontWeight: 800, fontSize: "21px", letterSpacing: "-0.4px" }}>
                Dockyard Software
              </p>
              <p style={{ margin: "5px 0 0", color: C.muted, fontSize: "12px" }}>POS System</p>
            </div>

            {/* ── Status badge ── */}
            <div style={{ display: "flex", justifyContent: "center", marginBottom: "22px" }}>
              <span style={{
                display: "inline-flex", alignItems: "center", gap: "8px",
                padding: "6px 18px", borderRadius: "999px",
                background: C.redDim, border: "1px solid rgba(239,68,68,0.22)",
                color: "#fb7185", fontSize: "12px", fontWeight: 700,
              }}>
                <span style={{
                  width: "7px", height: "7px", borderRadius: "50%",
                  background: "#fb7185", animation: "lic-pulse 1.9s ease-in-out infinite",
                }} />
                {hasExistingKey ? "License Inactive / Expired" : "License Required"}
              </span>
            </div>

            {/* ── Subtitle ── */}
            <p style={{
              margin: "0 0 22px", color: C.muted, fontSize: "13.5px",
              textAlign: "center", lineHeight: 1.7,
            }}>
              {hasExistingKey
                ? "Your license has been deactivated or has expired. Please enter a valid license key to restore full system access."
                : "A valid license key is required to use this system. Enter your key below or contact us to obtain one."}
            </p>

            {/* ── Key input ── */}

            <div style={{ marginBottom: "6px" }}>
              <label style={{
                display: "block", color: C.muted, fontSize: "11px",
                fontWeight: 700, letterSpacing: "0.07em",
                textTransform: "uppercase", marginBottom: "9px",
              }}>
                Enter License Key
              </label>
              <div style={{ display: "flex", gap: "9px" }}>
                <input
                  className="lic-input"
                  type="text"
                  placeholder="LIC-XXXX-XXXX-XXXX"
                  value={inputKey}
                  onChange={(e) => { setInputKey(e.target.value); setError(""); setSuccess(""); }}
                  onKeyDown={(e) => e.key === "Enter" && handleActivate()}
                  spellCheck={false}
                  autoComplete="off"
                  style={{
                    flex: 1, background: C.inputBg,
                    border: `1.5px solid ${C.cardBorder}`,
                    borderRadius: "12px", padding: "12px 15px",
                    color: C.text, fontSize: "13px", fontFamily: "monospace",
                    letterSpacing: "0.05em", outline: "none",
                    transition: "border-color 0.2s, box-shadow 0.2s",
                  }}
                />
                <button
                  className="lic-btn-act"
                  onClick={handleActivate}
                  disabled={activating}
                  style={{
                    padding: "12px 22px", borderRadius: "12px",
                    background: activating
                      ? C.divider
                      : "linear-gradient(135deg,#7c3aed,#6d28d9)",
                    color: activating ? C.dim : "#fff",
                    border: "none", cursor: activating ? "not-allowed" : "pointer",
                    fontWeight: 700, fontSize: "13px", whiteSpace: "nowrap",
                    display: "flex", alignItems: "center", gap: "8px",
                    transition: "opacity 0.2s, transform 0.15s",
                  }}
                >
                  {activating && (
                    <span style={{
                      width: "13px", height: "13px", display: "inline-block",
                      border: `2px solid ${C.cardBorder}`, borderTopColor: C.purpleL,
                      borderRadius: "50%", animation: "lic-spin 0.72s linear infinite",
                    }} />
                  )}
                  {activating ? "Checking…" : "Activate"}
                </button>
              </div>
            </div>

            {/* ── Error / success ── */}
            {error && (
              <div style={{
                display: "flex", alignItems: "flex-start", gap: "9px",
                color: "#fb7185", fontSize: "12.5px", marginTop: "11px",
                padding: "11px 13px", borderRadius: "10px",
                background: C.redDim, border: "1px solid rgba(239,68,68,0.18)",
              }}>
                <AlertIco size={14} stroke="#fb7185" style={{ flexShrink: 0, marginTop: "1px" }} />
                {error}
              </div>
            )}
            {success && (
              <div style={{
                display: "flex", alignItems: "center", gap: "9px",
                color: "#4ade80", fontSize: "12.5px", marginTop: "11px",
                padding: "11px 13px", borderRadius: "10px",
                background: C.greenDim, border: "1px solid rgba(34,197,94,0.18)",
              }}>
                <CheckIco size={14} stroke="#4ade80" />
                {success}
              </div>
            )}

            {/* ── Divider ── */}
            <div style={{ borderTop: `1px solid ${C.divider}`, margin: "26px 0 20px" }} />

            {/* ── Contact us button ── */}
            <button
              className="lic-contact-btn"
              onClick={() => setShowContact(true)}
              style={{
                width: "100%", padding: "13px",
                background: C.purpleDim,
                border: `1px solid ${C.purpleGlow}`,
                borderRadius: "13px", cursor: "pointer",
                color: C.purpleL, fontWeight: 700, fontSize: "13.5px",
                display: "flex", alignItems: "center", justifyContent: "center", gap: "9px",
                transition: "background 0.2s",
              }}
            >
              <PhoneIco size={16} stroke="currentColor" />
              Contact Us to Get / Renew a License
            </button>

            {/* ── Footer ── */}
            <p style={{
              margin: "22px 0 0", color: C.dim, fontSize: "11px", textAlign: "center",
            }}>
              © {new Date().getFullYear()} Dockyard Software · All rights reserved
            </p>
          </div>
        </div>
      </div>
    </>
  );
}
